import {
  Outlet,
  useLoaderData,
  useLocation,
  useMatches
} from "@remix-run/react"
import { DateTime } from "luxon"
import { type FC } from "react"
import DefaultErrorBoundary from "~/components/DefaultErrorBoundary"
import Footer from "~/components/Footer"
import Header from "~/components/Header"
import SiteBanner from "~/components/SiteBanner"
import { UtilityBar } from "~/components/design-system/organisms/UtilityBar"
import type { StoreHours } from "~/graphql"
import { useCustomerAccount } from "~/hooks/useCustomerAccount"
import useFocusOnRouteChange from "~/hooks/useFocusOnRouteChange"
import { pageLoader } from "~/loaders/pageLoader.server"
import { HeaderLocationPickerProvider } from "~/contexts/HeaderLocationPickerContext"
import { useAnalytics } from "~/providers/AnalyticsProvider"
import { GrowthBookExperiment } from "~/providers/GrowthBookProvider"

export const loader = pageLoader

export const ErrorBoundary = DefaultErrorBoundary

const Page: FC = () => {
  const {
    locationPages,
    experiences,
    eventLinks,
    activityLinks,
    siteBannerLink,
    stories,
    shopPromoLinks
  } = useLoaderData<typeof loader>()

  const { loggedIn, customer } = useCustomerAccount()

  const matches = useMatches()

  type LocationMatchData = {
    location?: {
      slug: string
      timezone: string
      storeHours?: StoreHours[] | null
    } | null
  }

  const locationData = matches
    .map(m => (m.data as LocationMatchData | undefined)?.location)
    .find(l => l != null)
  const hoursText = (() => {
    if (!locationData) return null
    const { timezone, storeHours } = locationData
    const todayName = DateTime.now()
      .setZone(timezone)
      .weekdayLong?.toLowerCase()
    const todayHours = storeHours?.find(h => h.day === todayName)
    if (todayHours?.from && todayHours?.to) {
      return `${todayHours.from} - ${todayHours.to}`
    }
    return "Closed"
  })()

  const locationSlug = locationData?.slug ?? null

  const utilityNavLinks = locationSlug
    ? [
        { label: "Deals", href: "/deals" },
        { label: "FAQ", href: "/help-center" },
        {
          label: "Getting Here",
          href: `locations/${locationSlug}#hours-and-directions`
        },
        { label: "Contact Us", href: "/contact" }
      ]
    : null

  const { pathname } = useLocation()
  const mainRef = useFocusOnRouteChange<HTMLElement>(pathname)
  const isBookingRoute = pathname.includes("/booking")

  const { identify } = useAnalytics()
  if (customer) {
    const email = customer.emailAddress?.emailAddress
    if (email) identify({ email })
  }

  return (
    <>
      <div className="relative flex min-h-screen flex-col max-w-camp-container-mobile md:max-w-camp-container-limit mx-auto">
        <HeaderLocationPickerProvider>
          <div className="relative flex flex-col">
            <SiteBanner link={siteBannerLink} />
          </div>
          {utilityNavLinks && !isBookingRoute ? (
            <UtilityBar hoursText={hoursText} navLinks={utilityNavLinks} />
          ) : null}

          <Header
            showSearch={!isBookingRoute}
            bookingMode={isBookingRoute}
            locationPages={locationPages}
            experiences={experiences}
            eventLinks={eventLinks}
            activityLinks={activityLinks}
            stories={stories}
            loggedIn={loggedIn}
            locationSlug={locationSlug}
            shopPromoLinks={shopPromoLinks}
          />

          <main ref={mainRef} tabIndex={-1} className="focus:outline-none">
            <Outlet />
          </main>
        </HeaderLocationPickerProvider>
      </div>
      <GrowthBookExperiment experiment="caid-test" variation="0">
        <div></div>
      </GrowthBookExperiment>
      <GrowthBookExperiment experiment="caid-test" variation="1">
        <div></div>
      </GrowthBookExperiment>
      <GrowthBookExperiment experiment="caid-test" variation="2">
        <div></div>
      </GrowthBookExperiment>
      <Footer locationPages={locationPages} />
    </>
  )
}

export default Page
